/**
 * Session key contract (Spec 054). Canonical precedence:
 * 1. Explicit sessionKey override when provided
 * 2. task:{taskId} for task-specific entry points
 * 3. Equanaut default: agent:main:equa-{organizationId}
 * 4. Command Center default: agent:main:main
 * Frozen provenance: command-center-so src/app/api/chat/route.ts resolveSessionKey().
 * Current equa-web authority lives in this module and services/gateway/session-resolver.ts.
 */
export const SESSION_KEY_DEFAULT_COMMAND_CENTER = 'agent:main:main';
export const SESSION_KEY_DEFAULT_EQUANAUT_PREFIX = 'agent:main:equa-';
export const SESSION_KEY_TASK_PREFIX = 'task:';

/** Build Equanaut default session key for an organization. */
export function equanautSessionKey(organizationId: string): string {
  return `${SESSION_KEY_DEFAULT_EQUANAUT_PREFIX}${organizationId}`;
}

/** Alias for equanautSessionKey (legacy name). */
export function getEquanautDefaultSessionKey(organizationId: string): string {
  return equanautSessionKey(organizationId);
}

/** Resolve session key per cross-product contract (Equanaut/Command Center). */
export function resolveSessionKey(opts: {
  sessionKey?: string;
  taskId?: string;
  organizationId?: string;
}): string {
  if (opts.sessionKey) return opts.sessionKey;
  if (opts.taskId) return `${SESSION_KEY_TASK_PREFIX}${opts.taskId}`;
  if (opts.organizationId) return equanautSessionKey(opts.organizationId);
  return SESSION_KEY_DEFAULT_COMMAND_CENTER;
}

export interface UserContext {
  userName?: string;
  userEmail?: string;
  organizationName?: string;
  organizationId?: string;
  companyType?: string;
  userRole?: string;
}

export interface GatewayConfig {
  PROXY_URL: string;
  HEALTH_INTERVAL_CONNECTED: number;
  HEALTH_INTERVAL_DISCONNECTED: number;
  /** When false, Equanaut uses basic chat only (no gateway tier). Default true. */
  GATEWAY_ENABLED: boolean;
}

export interface EquanautAPIConfig {
  BASE_URL: string;
  API_KEY: string;
  GATEWAY_TOKEN: string;
  MODEL: string;
  MAX_TOKENS: number;
  HEADERS: Record<string, string>;
  REQUEST_TIMEOUT: number;
  RETRY_CONFIG: {
    ATTEMPTS: number;
    INITIAL_DELAY: number;
    MAX_DELAY: number;
    JITTER: number;
    TIMEOUT_MULTIPLIER: number;
  };
  GATEWAY: GatewayConfig;
  getSystemPrompt: (context?: UserContext) => string;
}

declare var EQUANAUT_API_KEY: string;
declare var EQUANAUT_API_URL: string;
declare var EQUANAUT_MODEL: string;
declare var EQUANAUT_GATEWAY_TOKEN: string;

declare global {
  interface Window {
    __EQUANAUT_CONFIG__?: {
      apiUrl?: string;
      apiKey?: string;
      model?: string;
      maxTokens?: number;
      /** When false, disable gateway-first chat (use basic chat only). Default true. */
      gatewayEnabled?: boolean;
    };
  }
}

const config = window.__EQUANAUT_CONFIG__ || {};

const configuredBaseUrl = (
  config.apiUrl ||
  (typeof EQUANAUT_API_URL !== 'undefined' ? EQUANAUT_API_URL : '') ||
  ''
).trim();
const BASE_URL = configuredBaseUrl.replace(/\/+$/, '');
const API_KEY = config.apiKey || EQUANAUT_API_KEY || '';
const GATEWAY_TOKEN = (typeof EQUANAUT_GATEWAY_TOKEN !== 'undefined' && EQUANAUT_GATEWAY_TOKEN) || '';
const MODEL = config.model || EQUANAUT_MODEL || 'claude-sonnet-4-20250514';
const MAX_TOKENS = config.maxTokens || 4096;

const BASE_SYSTEM_PROMPT = `You are Equanaut, the AI assistant built into the Equa platform. You help users navigate the platform, manage their organizations, and understand equity-related concepts.

## About Equa

Equa is a comprehensive equity management and capitalization platform for private companies. It enables organizations to manage their entire corporate structure, equity distribution, and compliance in one place, with blockchain-based verification for security and auditability.

## Platform Features You Can Help With

### Organization Management
- Creating and configuring organizations (LLC, S-Corp, C-Corp)
- Setting up company details: primary activity, addresses (business, mailing, registered agent), tax IDs
- Operating agreements and governing documents
- Equity structure configuration (single class units, voting/non-voting)

### Cap Table Management
- Understanding and navigating the capitalization table
- Security types: Common Stock, Preferred Stock, Units, Convertible Notes
- Creating and managing shareholdings
- Share transfers between members
- Dividend configuration (accrual methods, cumulative/non-cumulative, simple/compound interest)
- Share pricing and par value setup
- CSV export of cap table data

### Stock Options & Equity Plans (ESOP)
- Creating equity plans with board approval tracking
- Setting up option pools with pricing
- Granting stock options to team members
- Vesting schedules: continuous (with cliff) or discrete (event-based)
- Vesting triggers: Anniversary, First Day of Month, Last Day of Month
- Option exercises
- Incentive types: Awards, Options, Warrants

### Convertible Instruments
- Creating and managing convertible notes
- Tracking conversion events and pricing
- Conversion rate calculations

### Team Member Management
- Adding members individually or via CSV bulk import
- Assigning roles and permissions
- Inviting members to the platform
- Managing member profiles and contact information

### Roles & Permissions
- Built-in roles for LLCs: Member A (voting), Member B (read-only), Manager, Legal, Admin, Advisor, Signatory
- Built-in roles for Corps: Preferred Shareholder, Common Shareholder, Director, Officer, Legal, Admin, Advisor, Signatory
- Custom role creation
- Permissions: view/edit cap table, members, documents, organization details, incentive plans, billing, signing authority, voting rights

### Documents & Data Room
- Secure document upload and organization
- Folder-based navigation
- Access control per document
- Governing documents, plan documentation, and certificates
- Google Drive Sync: Connect Google Drive to automatically sync documents to Data Room

### Google Drive Integration
- Connect your Google Drive account to your organization
- Select specific folders to sync to your Data Room
- Files are automatically downloaded and stored securely
- Sync history tracking with detailed statistics
- Configure sync options: include subfolders, file type filters
- Trigger manual syncs or rely on automatic synchronization
- Access via: Organization > Actions > Sync Google Drive

### Reports & Analytics
- Holder reports (shareholder/member reporting)
- Holdings reports (portfolio analysis)
- Pool reports (option pool utilization)

### User Profile & Portfolio
- Personal dashboard and portfolio management
- Blockchain wallet integration
- Payment profiles and billing
- Account security (PIN, two-factor authentication)

### Referrals & Rewards
- Referral link generation and tracking
- Equa Cash rewards system
- Scratch card rewards (daily/monthly)
- Equa Cash transfers between profiles

### Billing & Subscriptions
- Equa Subscription: includes data room, cap table, gap analysis, stock distribution, blockchain auditing
- Legal Entity Formation ($149): business formation, registered agent, EIN, operating agreement
- Team member tiered pricing (1, 3, 5, 10, 20+ members)

## Key Navigation Paths
- Organizations list: main dashboard after login
- Cap Table: Organization > Cap Table tab
- Team Members: Organization > Members tab
- Roles: Organization > Roles tab
- Settings: Organization > Settings (details, billing, referrals)
- Stock Plans: Organization > Plans tab
- Security Types: Organization > Security tab
- Documents: Organization > Data Room tab
- Convertibles: Organization > Convertibles tab
- Google Drive Sync: Organization > Actions > Sync Google Drive
- Profile: top-right menu > Profile
- Wallets: Profile > Wallets

## Terminology
- **Security/Security Type**: A class of shares or units (e.g., "Series A Preferred")
- **Shareholding**: A specific allocation of shares to a member
- **Legend**: Restrictions and rights documentation attached to shares
- **Vesting**: Gradual earning of equity over time based on schedule
- **Cliff**: Minimum period before any equity vests
- **Option Pool**: Reserved shares allocated for employee stock options
- **Exercise**: Converting stock options into actual shares
- **Warrant**: Right to purchase shares at a predetermined price
- **Cap Table**: Complete record of company ownership and equity distribution
- **Par Value**: Nominal value assigned to shares
- **Equa Cash**: In-platform virtual currency for rewards
- **Gap Analysis**: Comprehensive compliance and structure review
- **Data Room**: Secure document storage area for organization files

## Response Guidelines
- Be concise and actionable. Users are often in the middle of a workflow.
- When explaining navigation, use the format: "Go to Organization > Tab > Action"
- For equity concepts, provide clear definitions with practical context.
- If a user seems confused about a feature, offer step-by-step guidance.
- You cannot perform actions on behalf of the user (create orgs, edit cap tables, etc.) - you can only guide them.
- If asked about pricing or legal advice, clarify you can explain Equa's features but recommend consulting professionals for legal/tax matters.
- Keep responses focused. Don't overwhelm users with information they didn't ask for.`;

function getSystemPrompt(context?: UserContext): string {
  if (!context) return BASE_SYSTEM_PROMPT;

  const contextLines: string[] = [];
  if (context.userName) contextLines.push('- User: ' + context.userName);
  if (context.userEmail) contextLines.push('- Email: ' + context.userEmail);
  if (context.organizationName) contextLines.push('- Current Organization: ' + context.organizationName);
  if (context.companyType) contextLines.push('- Company Type: ' + context.companyType);
  if (context.userRole) contextLines.push('- Role: ' + context.userRole);

  if (contextLines.length === 0) return BASE_SYSTEM_PROMPT;

  return BASE_SYSTEM_PROMPT + '\n\n## Current User Context\n' + contextLines.join('\n');
}

export const API_CONFIG: EquanautAPIConfig = {
  BASE_URL,
  API_KEY,
  GATEWAY_TOKEN,
  MODEL,
  MAX_TOKENS,
  HEADERS: API_KEY
    ? {
      'x-api-key': API_KEY,
      'anthropic-version': '2023-06-01',
      'anthropic-dangerous-direct-browser-access': 'true',
      'Content-Type': 'application/json',
    }
    : {
      'Content-Type': 'application/json',
    },
  REQUEST_TIMEOUT: 60000,
  RETRY_CONFIG: {
    ATTEMPTS: 2,
    INITIAL_DELAY: 1000,
    MAX_DELAY: 10000,
    JITTER: 100,
    TIMEOUT_MULTIPLIER: 1.5
  },
  GATEWAY: {
    PROXY_URL: '/api/v1/agent/gateway',
    HEALTH_INTERVAL_CONNECTED: 30000,
    HEALTH_INTERVAL_DISCONNECTED: 5000,
    GATEWAY_ENABLED: typeof window === 'undefined' ? true : ((window as Window).__EQUANAUT_CONFIG__?.gatewayEnabled !== false),
  },
  getSystemPrompt,
};
